import { Observable } from 'rxjs/Observable';
import { CommonHttpService } from '../../shared/services/http.service';
import { Injectable } from '@angular/core';
import { AddressModel } from '../../shared/models/address.model';
import { Response } from '@angular/http';
import { Subject } from 'rxjs/Subject';
import { AuthenticatedUserRepositoryService } from './authenticated-user-repository.service';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/map';
import { ZipCodeRangeModel } from '../../shared/models/zip-code-range.model';
import { FreightCalculatorService } from '../../shared/services/freight-calculator.service';
import { DataLayerService } from '../../shared/data-layer/data-layer.service';

@Injectable()
export class DeliveryStepsService {
    private loadingAddressesSource: Subject<null> = new Subject();
    private loadedAddressesSource: Subject<Array<AddressModel>> = new Subject();
    private loadingFreightsSource: Subject<null> = new Subject();
    private loadedFreightsSource: Subject<Array<ZipCodeRangeModel>> = new Subject();
    private freightsResetedSource: Subject<null> = new Subject();
    private selectedAddressSource: Subject<AddressModel> = new Subject();
    private selectingFreightSource: Subject<ZipCodeRangeModel> = new Subject();
    private selectedFreightSource: Subject<ZipCodeRangeModel> = new Subject();
    private resetedSelectedAddressSource: Subject<AddressModel> = new Subject();
    private resetingSelectedFreightSource: Subject<null> = new Subject();
    private resetedSelectedFreightSource: Subject<ZipCodeRangeModel> = new Subject();

    protected addresses: Array<AddressModel> = [];
    protected selectedAddress: AddressModel = null;
    protected freights: Array<ZipCodeRangeModel> = [];
    protected selectedFreight: ZipCodeRangeModel = null;

    public loadedAddresses$ = this.loadedAddressesSource.asObservable();
    public loadingAddresses$ = this.loadingAddressesSource.asObservable();
    public loadedFreights$ = this.loadedFreightsSource.asObservable();
    public loadingFreights$ = this.loadingFreightsSource.asObservable();
    public resetedFreights$ = this.freightsResetedSource.asObservable();
    public selectedAddress$ = this.selectedAddressSource.asObservable();
    public selectingFreight$ = this.selectingFreightSource.asObservable();
    public selectedFreight$ = this.selectedFreightSource.asObservable();
    public resetedSelectedAddress$ = this.resetedSelectedAddressSource.asObservable();
    public resetingSelectedFreight$ = this.resetingSelectedFreightSource.asObservable();
    public resetedSelectedFreight$ = this.resetedSelectedFreightSource.asObservable();

    public inputtedZipCode: string = null;

	private resetInProgress$: any = null;
	public isApplyingFreight      = false;

    constructor(
        protected http: CommonHttpService,
        protected authenticatedUserService: AuthenticatedUserRepositoryService,
        protected freightCalculatorService: FreightCalculatorService,
        protected dataLayer: DataLayerService
    ) {
        this.authenticatedUserService.resetedUser$.subscribe(() => {
            this.reset();
        });
    }

    /**
     * Reset addresses and selectedAddress properties.
     **/
    public reset() {
        this.addresses = [];
        this.selectedAddress = null;
    }
    /**
     * Reset freights and selectedFreight properties.
     **/
    public resetFreights() {
        this.freights = [];
        this.selectedFreight = null;
        this.freightsResetedSource.next(null);
    }
    /**
     * Load all addresses from API to logged user.
     **/
    public loadAddresses() {
        this.loadingAddressesSource.next(null);
        return new Observable(observable => {
            this.http.useAuth().get(
                '/ecommerce/identification/addresses' +
                '?_limit=5' +
                '&_sort=-deliveries->order->datetime'
            ).map(
                res => res.json().data
            ).subscribe((res) => {
                this.addresses = res;
                this.loadedAddressesSource.next(this.addresses);
                observable.next(res);
                observable.complete();
            });
        });
    }
    /**
     * Load freight options from API to logged user.
     **/
    public loadFreights() {
        this.loadingFreightsSource.next(null);
        return new Observable(observable => {
            if (this.selectedAddress === null) {
                this.freights = [];
                this.loadedFreightsSource.next(this.freights);
                observable.next(this.freights);
                observable.complete();
            } else {
                this.freightCalculatorService
                    .calculate(this.selectedAddress.zip_code)
                    .subscribe((options: Array<ZipCodeRangeModel>) => {
                        this.freights = options;
                        this.loadedFreightsSource.next(this.freights);
                        observable.next(this.freights);
                        observable.complete();
                    });
            }
        });
    }
    /**
     * 'addresses' property getter.
     **/
    public getAddresses() {
        return this.addresses;
    }
    /**
     * 'freights' property getter.
     **/
    public getFreights(): Array<ZipCodeRangeModel> {
        return this.freights;
    }
    /**
     * 'selectedAddress' property getter.
     **/
    public getSelectedAddress() {
        return this.selectedAddress;
    }
    /**
     * Verify if has selectedAddress.
     **/
    public hasSelectedAddress(): boolean {
        return this.selectedAddress !== null;
    }
    /**
     * 'selectedAddress' property setter.
     **/
    public setSelectedAddress(address: AddressModel) {
        this.selectedAddress = address;
        this.selectedAddressSource.next(address);
    }
    /**
     * 'selectedFreight' property setter.
     **/
    public setSelectedFreight(freight: ZipCodeRangeModel, zipCode: string = null) {
	    if (null === zipCode) {
	        zipCode = this.selectedAddress.zip_code;
	    }

	    this.isApplyingFreight = true;

	    const doPost = () => {
	        this.selectingFreightSource.next(freight);
	        this.selectedFreight = freight;

	        this.http
	            .post('/checkout/zip-code-ranges', {
	                zip_code: zipCode,
	                zip_code_range: freight.id
	            })
	            .subscribe(() => {
	                this.dataLayer.selectFreight(freight);
	                this.selectedFreightSource.next(freight);
	                this.isApplyingFreight = false;
	            });
	    };

	    if (this.resetInProgress$) {
	        this.resetInProgress$.subscribe(() => doPost());
	    } else {
	        doPost();
	    }
	}
    /**
     * Selected address resetter.
     **/
    public resetSelectedAddress() {
        this.selectedAddress = null;
        this.resetFreights();
        this.resetedSelectedAddressSource.next(null);
    }
    /**
     * Selected freight resetter.
     **/
    public resetSelectedFreight() {
	    this.resetingSelectedFreightSource.next(null);
	    this.selectedFreight = null;

	    this.resetInProgress$ = this.http.delete('/checkout/zip-code-ranges');

	    this.resetInProgress$.subscribe(() => {
	        this.resetedSelectedFreightSource.next(null);
	        this.resetInProgress$ = null;
	    });
	}
    /**
     * Call API to save new address to logged user.
     **/
    public create(address: AddressModel) {
        return new Observable(observer => {
            this.http
                .useAuth()
                .post(
                    '/ecommerce/identification/addresses',
                    this.sanitizeZipCode(address)
                )
                .subscribe((res: Response) => {
                    if (res.status === 201) {
                        address.id = res.json().data.id;
                        this.addresses.unshift(address);
                        this.setSelectedAddress(address);
                        observer.next(address);
                    }
                    observer.complete();
                }, (res: Response) => {
                    observer.error(res.json().errors);
                }
            );
        });
    }
    /**
     * Call API to remove and create new address to logged user.
     **/
    public update(address: AddressModel) {
        return new Observable(observable => {
            this.remove(address).subscribe(() => {
                this.create(address).subscribe((res) => {
                    observable.next(res);
                    observable.complete();
                }, (errors) => {
                    observable.error(errors);
                });
            }, (errors) => {
                observable.error(errors);
            });
        });
    }
    /**
     * Call API to remove address from logged user.
     **/
    private remove(addressToDelete: AddressModel) {
        return new Observable(observer => {
            this.http.useAuth().delete(
                `/ecommerce/identification/addresses/${addressToDelete.id}`
            ).subscribe(() => {
                this.addresses = this.addresses.filter((address: AddressModel) => {
                    return (address.id !== addressToDelete.id);
                });
                observer.next(null);
                observer.complete();
            }, () => {
                observer.complete();
            });
        });
    }
    /**
     * This must keep only digits at zip code from address instance.
     **/
    private sanitizeZipCode(address: AddressModel): AddressModel {
        address.zip_code = address.zip_code.replace(/\D/g, '');

        return address;
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src1143190010/src/src/app/payment/services/delivery-steps.service.ts